import React from 'react';
import { offersSort } from 'helpers/offers-sort';
import { OffersActions, OffersState, SortType } from '../../../../reducers/offers';
import { useDispatch, useSelector } from '@ecommerce/nest-ssr/common';
import dealerDestructure from 'helpers/dealer-destructure';
import OfferInterface from 'interfaces/offer.interface';
import ProductOffersFilter from 'components/product/product-offers-filter/product-offers-filter';
import ProductOffersSort from 'components/product/product-offers-sort/product-offers-sort';
import ProductOfferWrapper from 'components/product/product-offer/product-offer-wrapper';
import ProductOffersTitle from 'components/product/product-offer/product-offers-title';
import Tooltip from 'atomic/tooltip';
import AdSlot from 'components/ads/ad-slot';
import { SLOT_OFFER_LIST_ELEMENT } from 'components/ads/ad-slot/ad-defs';

import './product-offers.scss';

const sortTitle = {
    [SortType.BestOffer]: 'najlepsze oferty wg Skąpiec.pl',
    [SortType.Price]: 'oferty od najtańszej',
    [SortType.ShopScore]: 'oferty od najwyższej oceny'
};

const OffersByScoreStrategy: React.FC<{ offers: OfferInterface[]; productName: string; }> = ({ offers, productName }) => {
    if (offers.length <= 4) {
        const first = offers.splice(0, 1).pop() as OfferInterface;
        const other = offers.sort(offersSort(SortType.Price));

        return (
            <>
                <ProductOfferWrapper offer={first}/>
                {other.length > 0 && <ProductOffersTitle productName={productName} description={'pozostałe oferty od najtańszej'}/>}
                {other.length > 0 && other.map(offer => <ProductOfferWrapper key={offer.id} offer={offer}/>)}
                <AdSlot className="product__content-main-ad" name={SLOT_OFFER_LIST_ELEMENT}/>
            </>
        );
    }

    const chunk1 = offers.splice(0, 4);
    const other = offers.sort(offersSort(SortType.Price));
    const chunk2 = other.splice(0, 3);
    const chunk3 = other;

    return (
        <>
            {chunk1.length > 0 && chunk1.map(offer => <ProductOfferWrapper key={offer.id} offer={offer}/>)}
            {chunk2.length > 0 && <ProductOffersTitle productName={productName} description={'pozostałe oferty od najtańszej'}/>}
            {chunk2.length > 0 && chunk2.map(offer => <ProductOfferWrapper key={offer.id} offer={offer}/>)}
            <AdSlot className="product__content-main-ad" name={SLOT_OFFER_LIST_ELEMENT}/>
            {chunk3.length > 0 && chunk3.map(offer => <ProductOfferWrapper key={offer.id} offer={offer}/>)}
        </>
    );
};

const OffersByPriceStrategy: React.FC<{ offers: OfferInterface[]; }> = ({ offers }) => {
    const chunk1 = offers.splice(0, 7);
    const chunk2 = offers;

    return (
        <>
            {chunk1.length > 0 && chunk1.map(offer => <ProductOfferWrapper key={offer.id} offer={offer}/>)}
            <AdSlot className="product__content-main-ad" name={SLOT_OFFER_LIST_ELEMENT}/>
            {chunk2.length > 0 && chunk2.map(offer => <ProductOfferWrapper key={offer.id} offer={offer}/>)}
        </>
    );
};

const ProductOffers: React.FC<{ productName: string; }> = ({ productName }) => {
    const dispatch = useDispatch();
    const offers = useSelector<{ offers: OffersState; }, OfferInterface[]>(
        state => state.offers.prepared.length > 0 ? [...state.offers.prepared] : [...state.offers.items]
    );
    const activeSort = useSelector<{ offers: OffersState; }, SortType>(state => state.offers.activeSort);
    const badgesCount = { isAuthorized: 0, freeDelivery: 0, delivery24h: 0, rates: 0, fastPayment: 0 };

    offers.forEach(offer => {
        const { freeDelivery, delivery24h, rates, fastPayment } = dealerDestructure(offer.dealer);
        badgesCount.freeDelivery += freeDelivery ? 1 : 0;
        badgesCount.delivery24h += delivery24h ? 1 : 0;
        badgesCount.rates += rates ? 1 : 0;
        badgesCount.fastPayment += fastPayment ? 1 : 0;
        badgesCount.isAuthorized += offer.dealer.isAuthorized ? 1 : 0;
    });

    const onFilterChange = (activeFiltersArray: string[]): void => {
        dispatch({ type: OffersActions.Filter, payload: { activeFilters: activeFiltersArray } });
    };

    const onSortChange = (selectedSort: SortType): void => {
        dispatch({ type: selectedSort });
    };

    return (
        <div className="product-offers-container">
            <div className="product-offers-header">
                <ProductOffersTitle productName={productName} description={`- ${sortTitle[activeSort]}`}>
                    {activeSort === SortType.BestOffer && (
                        <Tooltip
                            positions={['top']}
                            trigger={<div className="tooltip-text__trigger">
                                <i
                                    className="icon-info"
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Otwórz info"
                                />
                            </div>}
                            title={<>
                                    Jak wybieramy najlepsze oferty?
                            </>}
                        >
                            <p className="tooltip-text">
                                Ranking oferty jest obliczany na podstawie m.in. ceny na tle konkurencji,
                                warunków i kosztów dostawy oraz opinii o sklepie.
                            </p>
                        </Tooltip>
                    )}
                </ProductOffersTitle>
                <div className="product-offers-header__buttons">
                    <ProductOffersFilter badgesCount={badgesCount} onFilterChange={onFilterChange}/>
                    <ProductOffersSort onSortChange={onSortChange}/>
                </div>
            </div>
            {activeSort === SortType.BestOffer && <OffersByScoreStrategy offers={offers} productName={productName}/>}
            {activeSort !== SortType.BestOffer && <OffersByPriceStrategy offers={offers}/>}
        </div>
    );
};

export default ProductOffers;
